<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue'
import {
  faChartLine,
  faUsers,
  faFileAlt,
  faCog,
  faCow,
  faWheatAwn,
  faTractor,
  faDollarSign,
  faBox,
  faBars,
  faTimes,
  faEllipsisH,
  faUser,
  faSignOutAlt,
  faShop,
  faChartBar,
  faChevronDown,
  faChevronRight,
  faSeedling,
  faMapMarkedAlt,
  faListAlt,
  faHome,
  faInfoCircle,
  faEnvelope,
  faMoneyBill,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faStethoscope,
  faSyringe,
  faClipboardList,
  faHeart
} from '@fortawesome/free-solid-svg-icons'

library.add(
  faMoneyBill,
  faChartLine,
  faUsers,
  faFileAlt,
  faCog,
  faCow,
  faWheatAwn,
  faTractor,
  faDollarSign,
  faBox,
  faBars,
  faTimes,
  faEllipsisH,
  faUser,
  faSignOutAlt,
  faShop,
  faChartBar,
  faChevronDown,
  faChevronRight,
  faSeedling,
  faMapMarkedAlt,
  faListAlt,
  faHome,
  faInfoCircle,
  faEnvelope,
  faAngleDoubleLeft,
  faAngleDoubleRight
)

// --- Types ---
type PathItem = {
  name: string
  icon: string
  path: string
  action?: never
  children?: PathItem[]
  isOpen?: never
}

type ActionItem = {
  name: string
  icon: string
  action: () => Promise<void>
  path?: never
  children?: never
  isOpen?: boolean
}

type DropdownItem = {
  name: string
  icon: string
  path?: never
  action?: never
  children: PathItem[]
  isOpen: boolean
}

type SidebarItem = PathItem | ActionItem | DropdownItem

// --- Store & Router ---
const authStore = useAuthStore()
const router = useRouter()

// --- State ---
const isCollapsed = ref(false) // Desktop collapsed state
const isMobileOpen = ref(false) // Mobile drawer state
const windowWidth = ref(window.innerWidth)
const isLoggingOut = ref(false)

// --- Navigation Items ---
const commonItems = ref<SidebarItem[]>([])

const equipmentManagementDropdown: DropdownItem = {
  name: 'EquipOps',
  icon: 'tractor',
  children: [
    { name: 'Equipment', icon: 'tractor', path: '/equipment' },
    { name: 'Maintenance', icon: 'cog', path: '/maintenance' },
    { name: 'Fuel', icon: 'money-bill', path: '/fuel' },
    { name: 'Usage', icon: 'list-alt', path: '/usage' },
  ],
  isOpen: false,
};

const farmActionsDropdown: DropdownItem = {
  name: 'Farm',
  icon: 'wheat-awn',
  children: [
    { name: 'Farm List', icon: 'info-circle', path: '/supervisor/farm' },
    { name: 'Farm Expenses', icon: 'dollar-sign', path: '/supervisor/farm/expenses' },
    { name: 'Farm Yields', icon: 'seedling', path: '/farm/yields' },
    { name: 'Yields History', icon: 'wheat-awn', path: '/farm/yields-history' },
    { name: 'Farm Sales', icon: 'money-bill', path: '/farm/transactions' },
  ],
  isOpen: false,
}
const livestockManagementDropdown: DropdownItem = {
  name: 'Livestock Mgt',
  icon: 'cow',
  children: [
    { name: 'Livestock List', icon: 'list-alt', path: '/supervisor/animals' },
    { name: 'Production', icon: 'clipboard-list', path: '/production' },
    { name: 'Breeding', icon: 'heart', path: '/breeding' },
    { name: 'Feeding', icon: 'wheat-awn', path: '/feeding' },
    { name: 'Health', icon: 'stethoscope', path: '/supervisor/health' },
    { name: 'Finance', icon: 'dollar-sign', path: '/finance' },
  ],
  isOpen: false,
}

const ShopsManagementDropdown: DropdownItem = {
  name: 'Shops',
  icon: 'shop',
  children: [
    { name: 'All Products', icon: 'box', path: '/shops/products' },
    { name: 'Register Product', icon: 'box', path: '/shops/products/register' },
    { name: 'Butcher - Sales', icon: 'shop', path: '/shops/butcher/sales' },
    { name: 'Butcher - Reports', icon: 'chart-bar', path: '/shops/butcher/reports' },
    { name: 'Grocery - Sales', icon: 'shop', path: '/shops/grocery/sales' },
    { name: 'Grocery - Reports', icon: 'chart-bar', path: '/shops/grocery/reports' },
    { name: 'LocalHarvest - Sales', icon: 'shop', path: '/shops/local-harvest/sales' },
    { name: 'LocalHarvest - Reports', icon: 'chart-bar', path: '/shops/local-harvest/reports' },
    { name: 'Outstanding Bills', icon: 'money-bill', path: '/shops/outstanding-bills' },
  ],
  isOpen: false,
}



const adminItems = ref<SidebarItem[]>([
  { name: 'Dashboard', icon: 'chart-line', path: '/admin' },
  { name: 'Staff', icon: 'users', path: '/admin/users' },
  { name: 'Workers', icon: 'users', path: '/workers' },
  farmActionsDropdown,
  equipmentManagementDropdown,
  livestockManagementDropdown,
  ShopsManagementDropdown,
  { name: 'Reports', icon: 'file-alt', path: '/admin/reports' },
  { name: 'Messages', icon: 'envelope', path: '/admin/messages' },
])

const customerItems: PathItem[] = [
  { name: 'Dashboard', icon: 'chart-line', path: '/customer' },
  { name: 'Livestock', icon: 'cow', path: '/farmer/livestock' },
  { name: 'Crops', icon: 'wheat-awn', path: '/farmer/crops' },
  { name: 'Equipment', icon: 'tractor', path: '/farmer/equipment' },
]

const supervisorItems = ref<SidebarItem[]>([
  { name: 'Overview', icon: 'chart-line', path: '/supervisor' },
  { name: 'Workers', icon: 'users', path: '/workers' },
  farmActionsDropdown,
  livestockManagementDropdown,
  ShopsManagementDropdown,
  equipmentManagementDropdown,
  { name: 'Messages', icon: 'envelope', path: '/admin/messages' },
])

const roleItems = computed(() => {
  switch (authStore.user?.role) {
    case 'ADMIN':
      return adminItems.value
    case 'CUSTOMER':
      return customerItems
    case 'SUPERVISOR':
      return supervisorItems.value
    default:
      return []
  }
})

const sidebarItems = computed(() => {
  return [...roleItems.value, ...commonItems.value]
})

// --- Logic ---

const handleResize = () => {
  windowWidth.value = window.innerWidth
  if (windowWidth.value >= 768) {
    isMobileOpen.value = false // Close mobile drawer on desktop resize
  } else {
    isCollapsed.value = false // Reset collapse state on mobile
  }
}

const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value
  // Dispatch event for App.vue to adjust margins
  window.dispatchEvent(new CustomEvent('sidebar-toggle', { detail: { collapsed: isCollapsed.value } }))
}

const toggleMobileMenu = () => {
  isMobileOpen.value = !isMobileOpen.value
}

const closeMobileMenu = () => {
  isMobileOpen.value = false
}

const toggleDropdown = (item: SidebarItem) => {
  if (isDropdownItem(item)) {
    // If collapsed, expanding a dropdown should expand the sidebar
    if (isCollapsed.value) {
      isCollapsed.value = false
      window.dispatchEvent(new CustomEvent('sidebar-toggle', { detail: { collapsed: false } }))
      setTimeout(() => {
        item.isOpen = true
      }, 150)
    } else {
      item.isOpen = !item.isOpen
    }
  }
}

const logout = async () => {
  isLoggingOut.value = true
  try {
    await authStore.logout()
    router.push('/login')
  } finally {
    isLoggingOut.value = false
    closeMobileMenu()
  }
}

// Helpers
const isPathItem = (item: SidebarItem): item is PathItem => 'path' in item && item.path !== undefined
const isActionItem = (item: SidebarItem): item is ActionItem => 'action' in item && item.action !== undefined
const isDropdownItem = (item: SidebarItem): item is DropdownItem => 'children' in item && item.children !== undefined

onMounted(() => {
  window.addEventListener('resize', handleResize)
  // Check localStorage for saved collapsed state preference if needed
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <LoadingSpinner v-if="isLoggingOut" message="Logging out..." />

  <!-- Mobile Hamburger (Fixed Top Left) -->
  <button
    @click="toggleMobileMenu"
    class="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white/80 backdrop-blur-md shadow-sm border border-gray-200 text-gray-700 hover:text-green-600 transition-all print:hidden"
    aria-label="Toggle Menu"
  >
    <FontAwesomeIcon icon="bars" class="h-5 w-5" />
  </button>

  <!-- Mobile Backdrop -->
  <div 
    v-if="isMobileOpen" 
    class="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm transition-opacity print:hidden"
    @click="closeMobileMenu"
  ></div>

  <!-- Sidebar Container -->
  <aside
    class="fixed top-0 left-0 z-50 h-screen bg-white shadow-xl flex flex-col transition-all duration-300 ease-in-out border-r border-gray-100 print:hidden"
    :class="[
      isCollapsed ? 'w-20' : 'w-64',
      isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
    ]"
  >
    <!-- Header / Logo Area -->
    <div class="h-20 flex items-center justify-between px-6 border-b border-gray-100/50">
      <div class="flex items-center gap-3 overflow-hidden" :class="{ 'justify-center w-full': isCollapsed }">
        <!-- Logo Icon -->
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-teal-500 flex items-center justify-center shadow-lg shadow-green-200 flex-shrink-0">
           <span class="text-white font-bold text-xl">R</span>
        </div>
        
        <!-- Logo Text (Hidden on Collapse) -->
        <div 
          class="flex flex-col transition-all duration-300"
          :class="{ 'opacity-0 w-0 translate-x-[-20px]': isCollapsed, 'opacity-100 w-auto': !isCollapsed }"
        >
          <span class="font-bold text-gray-800 text-lg leading-tight tracking-tight">Rochex</span>
          <span class="text-xs text-green-600 font-medium tracking-wide">INVESTMENTS</span>
        </div>
      </div>
    </div>

    <!-- Toggle Button (Desktop Only) -->
    <button 
      @click="toggleCollapse"
      class="absolute -right-3 top-24 w-6 h-6 bg-white border border-gray-200 rounded-full shadow-md flex items-center justify-center text-gray-400 hover:text-green-600 transition-colors z-50 hidden md:flex"
    >
      <FontAwesomeIcon :icon="isCollapsed ? 'angle-double-right' : 'angle-double-left'" class="h-3 w-3" />
    </button>

    <!-- Scrollable Navigation -->
    <div class="flex-1 overflow-y-auto py-6 px-3 custom-scrollbar">
      <ul class="space-y-1">
        <li v-for="item in sidebarItems" :key="item.name">
          
          <!-- Single Link Item -->
          <RouterLink
            v-if="isPathItem(item)"
            :to="item.path"
            class="group flex items-center rounded-xl transition-all duration-200"
            :class="[
              isCollapsed ? 'justify-center p-3' : 'px-4 py-3',
              $route.path === item.path 
                ? 'bg-green-50 text-green-700 shadow-sm shadow-green-100' 
                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900 icon-hover'
            ]"
            @click="closeMobileMenu"
          >
            <div class="relative" :class="{ 'tooltip-container': isCollapsed }">
              <FontAwesomeIcon 
                :icon="item.icon" 
                class="h-5 w-5 transition-transform group-hover:scale-110"
                :class="$route.path === item.path ? 'text-green-600' : 'text-gray-400 group-hover:text-gray-600'" 
              />
              <!-- Tooltip for Collapsed Mode -->
              <span v-if="isCollapsed" class="tooltip-text">{{ item.name }}</span>
            </div>
            
            <span 
              v-if="!isCollapsed" 
              class="ml-3 font-medium text-sm whitespace-nowrap"
            >
              {{ item.name }}
            </span>
          </RouterLink>

          <!-- Dropdown Item -->
          <div v-else-if="isDropdownItem(item)" class="relative">
            <button
              @click="toggleDropdown(item)"
              class="w-full group flex items-center rounded-xl transition-all duration-200 justify-between"
              :class="[
                isCollapsed ? 'justify-center p-3' : 'px-4 py-3',
                item.isOpen ? 'bg-gray-50 text-gray-900' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900 icon-hover'
              ]"
            >
              <div class="flex items-center">
                <div class="relative" :class="{ 'tooltip-container': isCollapsed }">
                  <FontAwesomeIcon 
                    :icon="item.icon" 
                    class="h-5 w-5 transition-transform group-hover:scale-110"
                    :class="item.isOpen ? 'text-green-600' : 'text-gray-400 group-hover:text-gray-600'" 
                  />
                  <span v-if="isCollapsed" class="tooltip-text">{{ item.name }}</span>
                </div>
                <span v-if="!isCollapsed" class="ml-3 font-medium text-sm whitespace-nowrap">{{ item.name }}</span>
              </div>
              
              <FontAwesomeIcon
                v-if="!isCollapsed"
                :icon="item.isOpen ? 'chevron-down' : 'chevron-right'"
                class="h-3 w-3 text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-180': item.isOpen }"
              />
            </button>

            <!-- Nested Children -->
            <transition
              enter-active-class="transition-all duration-300 ease-out"
              enter-from-class="max-h-0 opacity-0"
              enter-to-class="max-h-96 opacity-100"
              leave-active-class="transition-all duration-200 ease-in"
              leave-from-class="max-h-96 opacity-100"
              leave-to-class="max-h-0 opacity-0"
            >
              <div v-show="item.isOpen && !isCollapsed" class="overflow-hidden bg-gray-50/50 rounded-lg mx-2 mt-1 space-y-1">
                <RouterLink
                  v-for="child in item.children"
                  :key="child.name"
                  :to="child.path"
                  class="flex items-center px-4 py-2 text-sm rounded-lg transition-colors group ml-2 border-l-2 border-transparent hover:border-green-300"
                  :class="$route.path === child.path ? 'text-green-700 bg-green-50/50 border-green-500 font-medium' : 'text-gray-500 hover:text-gray-900'"
                  @click="closeMobileMenu"
                >
                  <span class="w-1.5 h-1.5 rounded-full mr-3 transition-colors" :class="$route.path === child.path ? 'bg-green-500' : 'bg-gray-300 group-hover:bg-green-400'"></span>
                  {{ child.name }}
                </RouterLink>
              </div>
            </transition>
          </div>
        </li>
      </ul>
    </div>

    <!-- Footer / Profile -->
    <div class="p-4 border-t border-gray-100 bg-gray-50/50">
      <div class="flex items-center" :class="{ 'justify-center': isCollapsed, 'justify-between': !isCollapsed }">
        <div class="flex items-center gap-3 overflow-hidden">
          <RouterLink to="/profile" class="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-600 font-bold border-2 border-white shadow-sm flex-shrink-0 cursor-pointer hover:bg-teal-200 transition-colors tooltip-container relative">
            {{ authStore.user?.username?.charAt(0).toUpperCase() || 'U' }}
             <span v-if="isCollapsed" class="tooltip-text-top">{{ authStore.user?.username }}</span>
          </RouterLink>
          
          <RouterLink v-if="!isCollapsed" to="/profile" class="flex flex-col">
            <span class="font-semibold text-gray-800 text-sm truncate w-32">{{ authStore.user?.username || 'User' }}</span>
            <span class="text-xs text-gray-500 capitalize">{{ authStore.user?.role?.toLowerCase() }}</span>
          </RouterLink>
        </div>

        <button 
          v-if="!isCollapsed"
          @click="logout"
          class="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
          title="Logout"
        >
          <FontAwesomeIcon icon="sign-out-alt" class="h-5 w-5" />
        </button>
      </div>
       <!-- Collapsed Logout Button (Separate logic for better UX) -->
       <button 
          v-if="isCollapsed"
          @click="logout"
          class="mt-3 w-10 h-10 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors mx-auto tooltip-container relative"
        >
          <FontAwesomeIcon icon="sign-out-alt" class="h-5 w-5" />
          <span class="tooltip-text-top">Logout</span>
        </button>
    </div>
  </aside>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #e5e7eb;
  border-radius: 20px;
}
.custom-scrollbar:hover::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
}

/* Tooltip Styling */
.tooltip-container:hover .tooltip-text {
  opacity: 1;
  visibility: visible;
  transform: translateY(-50%) translateX(0);
}

.tooltip-text {
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%) translateX(-10px);
  margin-left: 10px;
  background-color: #1f2937;
  color: white;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 50;
  pointer-events: none;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.tooltip-text::before {
  content: '';
  position: absolute;
  left: -4px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 4px;
  border-style: solid;
  border-color: transparent #1f2937 transparent transparent;
}

/* Tooltip Top Logic */
.tooltip-container:hover .tooltip-text-top {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.tooltip-text-top {
  position: absolute;
  left: 100%;
  bottom: 50%;
  transform: translateY(10px);
  margin-left: 10px;
  background-color: #1f2937;
  color: white;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 50;
  pointer-events: none;
}
</style>